import {take, put, fork, select} from 'redux-saga/effects';
import {v4 as uuidv4} from 'uuid';
import {openModal, closeModal} from '@computerrock/formation-router/sagas';
import fetchRequest from '../../application/sagas/fetchRequest';
import * as serviceAssignmentActionTypes from '../serviceAssignmentActionTypes';
import * as serviceCaseActionTypes from '../../service-cases/serviceCaseActionTypes';
import config from '../../config';
import ecsAccessControl from '../../ecsAccessControl';
import {ecsFeatures, ecsFeatureActions} from '../../application/ecsFeatures';
import modalIds from '../../modalIds';
import * as applicationActionTypes from '../../application/applicationActionTypes';

/**
 * Update ServiceAssignment
 */
const updateServiceAssignment = function* updateServiceAssignment(params) {
    const {caller, assignmentType, serviceAssignmentLineNo, serviceCaseId, serviceAssignmentData} = params;
    const {serviceManager} = yield select(state => state.application);
    const ecsFlowService = serviceManager.loadService('ecsFlowService');
    const serviceCase = yield select(state => state.serviceCases.serviceCases[serviceCaseId]);
    const isAssignmentUpdateAvailable = ecsAccessControl.grantFeatureAccess(
        ecsFeatures.SERVICE_ASSIGNMENT,
        ecsFeatureActions.UPDATE,
    );
    const isCaseLockUpdateAvailable = ecsAccessControl.grantFeatureAccess(
        ecsFeatures.CASE_LOCK,
        ecsFeatureActions.UPDATE,
    );

    if (!serviceCase || !isAssignmentUpdateAvailable || !isCaseLockUpdateAvailable) return;

    const lockUUID = serviceCase.lock ? serviceCase.lock.lockUUID : null;

    // call lock endpoint
    yield fork(fetchRequest, serviceCaseActionTypes.LOCK_SERVICE_CASE_REQUEST, ecsFlowService.lockServiceCase, {
        serviceCaseId,
        lockData: {lockUUID: lockUUID || uuidv4()},
    }, {
        caller,
    });

    let lockResponse = {};
    while (true) {
        lockResponse = yield take([
            serviceCaseActionTypes.LOCK_SERVICE_CASE_REQUEST_SUCCEEDED,
            serviceCaseActionTypes.LOCK_SERVICE_CASE_REQUEST_FAILED,
        ]);

        if (lockResponse.meta?.caller && lockResponse.meta.caller === caller) {
            break;
        }
    }

    // if case is not locked or case is lockedBy current user -> allow patch
    if (!lockResponse.error && lockResponse.type === serviceCaseActionTypes.LOCK_SERVICE_CASE_REQUEST_SUCCEEDED) {
        yield put({
            type: serviceCaseActionTypes.SET_SERVICE_CASE_REMAINING_LOCK_TIME,
            payload: {remainingLockTime: config.DEFAULT_SERVICE_CASE_LOCK_TIME},
        });

        yield put({
            type: serviceAssignmentActionTypes.BATCH_UPDATE_SERVICE_ASSIGNMENT,
            payload: {assignmentType, serviceAssignmentLineNo, serviceCaseId, serviceAssignmentData},
            meta: {caller},
        });

        let batchUpdateServiceAssignmentResponse;
        while (true) {
            const batchUpdateServiceAssignmentAction = yield take(
                serviceAssignmentActionTypes.BATCH_UPDATE_SERVICE_ASSIGNMENT_COMPLETED,
            );
            const {meta = {}} = batchUpdateServiceAssignmentAction;
            if (typeof meta.caller !== 'undefined' && meta.caller !== caller) continue;

            batchUpdateServiceAssignmentResponse = batchUpdateServiceAssignmentAction;
            break;
        }

        if (batchUpdateServiceAssignmentResponse.error) {
            const {message = ''} = batchUpdateServiceAssignmentResponse.payload;
            const parsedMessage = message ? JSON.parse(message) : null;

            if (!parsedMessage || !parsedMessage.errors?.length) return;

            const {errors} = parsedMessage;
            yield* openModal(modalIds.ERROR_MESSAGE_MODAL, {
                actionType: serviceAssignmentActionTypes.UPDATE_SERVICE_ASSIGNMENT_REQUEST_FAILED,
                ...(errors.length > 0
                    && {
                        errors: JSON.stringify(errors),
                    }),
            });

            yield take([
                applicationActionTypes.CANCEL_REQUEST,
            ]);

            yield* closeModal(modalIds.ERROR_MESSAGE_MODAL, {actionType: '', errors: ''});
        }

        return batchUpdateServiceAssignmentResponse;
    }

    return lockResponse;
};

export default updateServiceAssignment;
